// The printed package label, shared by both ends of the route.
//
// A package looks the same whichever way it travels, so this is the single
// definition of that label: the branded route band (origin — truck on a dashed
// track — destination), the QR block, and the checklist. Central used to have
// its own plainer builder with no logo and no route band, which meant the same
// physical box printed two different ways depending on who pressed Print.
//
// `pkg` is a hydrated package (packageNumber, qrDataUrl, orders,
// stockTransferRequests, originStore, currentLocation, status).

import i18n from '../i18n';

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Translucent shades of the store colour for the route track and badge fill.
// Falls back to the raw value if the theme isn't a plain hex.
const tint = (color, alpha) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(color || '').trim());
  if (!m) return color;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const TRUCK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M14 17V6a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2"/>
  <path d="M14 9h4l3 3.2V16a1 1 0 0 1-1 1h-1"/>
  <circle cx="7" cy="17.5" r="2.1"/><circle cx="17" cy="17.5" r="2.1"/></svg>`;

// Solid arrowhead — at this size an outlined chevron prints thin and weak.
const TIP_SVG = `<svg viewBox="0 0 12 16" aria-hidden="true">
  <path d="M1 1 11 8 1 15Z" fill="currentColor"/></svg>`;

export function buildPackageLabelHtml(pkg, options = {}) {
  const {
    branding = {},
    defaultBranding = {},
    centralStoreName = i18n.t('packageLabel.central'),
  } = options;

  const isTransfer = pkg.packageType === 'stock_transfer';
  const ords = Array.isArray(pkg.orders) ? pkg.orders : [];
  const transfers = Array.isArray(pkg.stockTransferRequests) ? pkg.stockTransferRequests : [];
  // Accessories are not on the package record; the caller that just packed
  // them passes them in so the box's label names them.
  const supplies = Array.isArray(pkg.supplies) ? pkg.supplies : [];

  const line = (i, main, note) =>
    `<li><span class="n">${i + 1}</span><span class="ref">${esc(main)}</span>${note ? `<span class="note">${esc(note)}</span>` : ''}</li>`;

  // A transfer line has to carry the power as well as the name — two rows of the
  // same lens at different SPH are different things on the shelf.
  // Everything in the box, in one numbered list: a box central packs for a
  // store can carry customer orders, lens transfers and accessories at once,
  // and a label that showed only one kind left the rest unlisted.
  const entries = [
    ...ords.map((o) => [o.orderNumber || '', '']),
    ...transfers.map((r) => [
      [r.productName || i18n.t('packageLabel.lens'), r.displayCorrection].filter(Boolean).join(' · '),
      `×${r.qty || 1}`,
    ]),
    ...supplies.map((s) => [s.productName || '', `×${s.qty || 1}`]),
  ];
  const items = entries.map(([main, note], i) => line(i, main, note)).join('');

  const countLabel = [
    ords.length ? i18n.t('packageLabel.orderCount', { count: ords.length }) : null,
    transfers.length ? i18n.t('packageLabel.stockRequestCount', { count: transfers.length }) : null,
    supplies.length ? i18n.t('packageLabel.accessoryCount', { count: supplies.length }) : null,
  ].filter(Boolean).join(' · ') || (isTransfer
    ? i18n.t('packageLabel.stockRequestCount', { count: 0 })
    : i18n.t('packageLabel.orderCount', { count: 0 }));
  const qrSrc = pkg.qrDataUrl || '';
  const qrValue = pkg.qrCode || pkg.packageNumber || '';

  // The package belongs to the store at the far end, so prefer its own branding
  // and fall back to the signed-in one. storeToApi emits '' for unset branding
  // fields, so drop the blanks before merging or they would wipe out the
  // colours we do have.
  const filled = (obj) => Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== '' && v != null)
  );
  const bnd = { ...defaultBranding, ...filled(branding), ...filled(pkg.originStore?.branding) };
  const primary = bnd.primaryColor || defaultBranding.primaryColor || '#2563eb';
  const storeName = pkg.originStore?.name || bnd.storeName || '';
  const logoSrc = bnd.logo ? (bnd.logo.startsWith('http') ? bnd.logo : `/${bnd.logo}`) : '';
  const storeMeta = [bnd.city, bnd.phone].filter(Boolean).join(' · ');

  // Which way the package travels. Outbound packages are created sitting at the
  // store, returns and transfers are created sitting at central, so the current
  // location tells us the direction.
  const locType = String(pkg.currentLocation?.type || '').toUpperCase();
  const toStore = locType === 'CENTRAL'
    || ['at_central_returned', 'at_central_outbound', 'in_transit_to_store'].includes(pkg.status);
  const storeSide = { name: storeName || '—', meta: storeMeta, logo: logoSrc };
  const centralSide = { name: centralStoreName, meta: '', logo: '' };
  const [from, to] = toStore ? [centralSide, storeSide] : [storeSide, centralSide];

  const endpoint = (side, align) => `<div class="end ${align}">
    ${side.logo ? `<img src="${esc(side.logo)}" alt="${esc(side.name)} logo"/>` : ''}
    <div class="who">
      <div class="name">${esc(side.name)}</div>
      ${side.meta ? `<div class="meta">${esc(side.meta)}</div>` : ''}
    </div>
  </div>`;

  const lang = i18n.language || 'en';
  return `<!DOCTYPE html><html lang="${esc(lang)}"><head><meta charset="utf-8"/><title>${esc(pkg.packageNumber || i18n.t('packageLabel.package'))}</title>
<style>
  @page { size: A5 portrait; margin: 8mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; padding: 0; color: #0f172a;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Route band: origin — truck on a dashed track — destination */
  .brand { display: flex; align-items: center; gap: 5px; border-bottom: 3px solid ${primary}; padding-bottom: 9px; }
  .end { display: flex; align-items: center; gap: 7px; flex: 1 1 0; min-width: 0; }
  .end.right { justify-content: flex-end; text-align: right; }
  .end img { max-height: 14mm; max-width: 22mm; object-fit: contain; }
  .end .who { min-width: 0; }
  .end .name { font-size: 15px; font-weight: 800; color: ${primary}; line-height: 1.15; }
  .end .meta { font-size: 9px; color: #475569; margin-top: 2px; }

  /* The truck rides a dashed track that runs from origin to destination, so the
     direction reads at a glance instead of from two small chevrons. */
  .route { flex: 0 0 40mm; display: flex; align-items: center; color: ${primary}; }
  .route .track { flex: 1 1 auto; height: 0; border-top: 2px dashed ${tint(primary, 0.45)}; }
  .route .badge { flex: 0 0 auto; width: 12mm; height: 12mm; border-radius: 50%;
    border: 2px solid ${primary}; background: ${tint(primary, 0.10)};
    display: flex; align-items: center; justify-content: center; }
  .route .badge svg { width: 7mm; height: 7mm; display: block; }
  .route .tip { flex: 0 0 auto; margin-left: -1px; }
  .route .tip svg { width: 3.6mm; height: 4.8mm; display: block; }

  .head { display: flex; gap: 12px; align-items: center; border-bottom: 2px solid #0f172a;
    padding: 10px 0; }
  .qr { flex: 0 0 auto; width: 34mm; text-align: center; }
  .qr img { width: 34mm; height: 34mm; display: block; }
  .qr code { display: block; margin-top: 3px; font-size: 8px; color: #475569; word-break: break-all; }
  .qr .missing { width: 34mm; height: 34mm; display: flex; align-items: center; justify-content: center;
    text-align: center; font-size: 10px; color: #94a3b8; border: 1px dashed #cbd5e1; border-radius: 8px; }
  .ident { min-width: 0; }
  .ident .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #94a3b8; font-weight: 800; }
  .ident .no { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 20px; font-weight: 800;
    line-height: 1.15; word-break: break-all; }
  .ident .sub { margin-top: 4px; font-size: 11px; color: #475569; }
  .ident .tag { display: inline-block; margin-top: 5px; font-size: 9px; font-weight: 800;
    text-transform: uppercase; letter-spacing: .08em; color: #fff; background: ${primary};
    border-radius: 999px; padding: 3px 9px; }

  ol { list-style: none; margin: 12px 0 0; padding: 0;
    display: grid; grid-template-columns: repeat(auto-fill, minmax(58mm, 1fr)); gap: 5px 8mm; }
  li { display: flex; align-items: baseline; gap: 7px; border-bottom: 1px dashed #e2e8f0; padding-bottom: 4px; }
  li .n { flex: 0 0 auto; min-width: 15px; font-size: 10px; font-weight: 700; color: #94a3b8; }
  li .ref { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; font-weight: 700;
    word-break: break-all; }
  li .note { margin-left: auto; font-size: 11px; font-weight: 700; color: #475569; }
  .empty { margin-top: 14px; font-size: 12px; color: #94a3b8; }
  .footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #64748b; }
</style>
</head><body>
<div class="brand">
  ${endpoint(from, 'left')}
  <div class="route">
    <span class="track"></span>
    <span class="badge">${TRUCK_SVG}</span>
    <span class="track"></span>
    <span class="tip">${TIP_SVG}</span>
  </div>
  ${endpoint(to, 'right')}
</div>
<div class="head">
  <div class="qr">
    ${qrSrc
      ? `<img src="${qrSrc}" alt="QR ${esc(pkg.packageNumber || '')}"/>`
      : `<div class="missing">${esc(i18n.t('packageLabel.qrUnavailable'))}</div>`}
    ${qrValue ? `<code>${esc(qrValue)}</code>` : ''}
  </div>
  <div class="ident">
    <div class="lbl">${esc(i18n.t('packageLabel.package'))}</div>
    <div class="no">${esc(pkg.packageNumber || '—')}</div>
    <div class="sub">${esc(countLabel)} · ${new Date().toLocaleDateString()}</div>
    <div class="tag">${esc(isTransfer ? i18n.t('packageLabel.stockTransfer') : i18n.t('packageLabel.packingList'))}</div>
  </div>
</div>
${items ? `<ol>${items}</ol>` : `<div class="empty">${esc(isTransfer ? i18n.t('packageLabel.noStockRequests') : i18n.t('packageLabel.noOrders'))}</div>`}
<div class="footer">${esc(i18n.t('packageLabel.footer'))}</div>
</body></html>`;
}

// Prints through a hidden iframe rather than window.open: a popup blocker kills
// the second one silently, and the operator just sees nothing happen.
export function openPrintIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  let printed = false;
  const run = () => {
    if (printed) return; printed = true;
    setTimeout(() => {
      try { iframe.contentWindow.focus(); } catch (_) {}
      try { iframe.contentWindow.print(); } catch (_) {}
      setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1500);
    }, 600);
  };
  iframe.onload = run;
  setTimeout(run, 1800);
}
